/**
 * Programmatic SEO — Page « SmartFacture vs [Concurrent] »
 * Target keyword: "smartfacture vs [competitor]" + "[competitor] alternative"
 */
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, X, Sparkles } from 'lucide-react';
import concurrents from '../../data/seo/concurrents.json';

const FEATURE_MATRIX = [
  { key: 'Format Factur-X (PDF/A-3 + XML)', sf: true, comp: false },
  { key: 'Copilote IA conversationnel français', sf: true, comp: false },
  { key: 'Scanner IA OCR (reçus, factures)', sf: true, comp: false },
  { key: 'Simulation PPF/PDP', sf: true, comp: false },
  { key: 'Plan gratuit sans CB (5 factures/mois)', sf: true, comp: false },
  { key: 'Module URSSAF intégré', sf: true, comp: false },
  { key: 'Conformité réforme 2026 garantie', sf: true, comp: false },
  { key: 'Relances automatiques', sf: true, comp: true },
  { key: 'Export comptable CSV/FEC', sf: true, comp: true },
  { key: 'Hébergement France (OVHcloud)', sf: true, comp: false },
];

export default function ComparaisonProgPage() {
  const { fullSlug } = useParams();

  // URL pattern is /comparaison/smartfacture-vs-<slug>
  // Strip the prefix; otherwise, fall through to 404 redirect
  const PREFIX = 'smartfacture-vs-';
  if (!fullSlug || !fullSlug.startsWith(PREFIX)) {
    return <Navigate to="/" replace />;
  }
  const slug = fullSlug.slice(PREFIX.length);
  const c = concurrents.find((x) => x.slug === slug);

  if (!c) {
    return <Navigate to="/" replace />;
  }

  const others = concurrents.filter((x) => x.slug !== c.slug).slice(0, 6);

  const title = `SmartFacture vs ${c.name} : alternative française 2026 | Comparatif`;
  const description = `Comparatif SmartFacture AI vs ${c.name} (${c.tagline}). Factur-X conforme 2026, Copilote IA français, scanner OCR. Essai gratuit sans CB.`;
  const url = `https://smartfacture-ai.com/comparaison/smartfacture-vs-${c.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://smartfacture-ai.com/' },
      { '@type': 'ListItem', position: 2, name: 'Comparaisons', item: 'https://smartfacture-ai.com/comparaison' },
      { '@type': 'ListItem', position: 3, name: `vs ${c.name}`, item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAFFF7]" data-testid="seo-comparaison-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <nav className="max-w-5xl mx-auto px-6 pt-8 pb-2 text-xs text-slate-500" aria-label="Fil d'Ariane">
        <Link to="/" className="hover:underline">Accueil</Link>
        <span className="mx-1.5">›</span>
        <Link to="/comparaison" className="hover:underline">Comparaisons</Link>
        <span className="mx-1.5">›</span>
        <span className="text-slate-900 font-medium">vs {c.name}</span>
      </nav>

      <section className="max-w-5xl mx-auto px-6 pt-8 pb-12">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold mb-5">
          <Sparkles className="h-3.5 w-3.5" />
          Comparatif détaillé 2026
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-5">
          SmartFacture AI <span className="text-slate-400">vs</span> <span className="text-emerald-600">{c.name}</span>
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mb-7">
          Vous hésitez entre SmartFacture AI et {c.name} ({c.tagline}) pour la conformité à la réforme de la facturation électronique 2026 ? Voici une comparaison détaillée et honnête.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/register" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5" data-testid="seo-comp-cta-register">
            Essayer SmartFacture <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Verdict box */}
      <section className="max-w-5xl mx-auto px-6 mb-12">
        <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-xl p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">Notre verdict</div>
          <p className="text-slate-700 leading-relaxed">{c.verdict}</p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-white py-12 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            Comparatif fonctionnalités SmartFacture vs {c.name}
          </h2>
          <p className="text-slate-600 mb-8">
            {c.weakness}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="seo-comp-table">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-3 text-sm font-semibold text-slate-600">Fonctionnalité</th>
                  <th className="py-3 px-3 text-sm font-semibold text-emerald-600">SmartFacture AI</th>
                  <th className="py-3 px-3 text-sm font-semibold text-slate-500">{c.name}</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_MATRIX.map((f) => (
                  <tr key={f.key} className="border-b border-slate-100 hover:bg-emerald-50/30">
                    <td className="py-3 px-3 text-sm text-slate-700">{f.key}</td>
                    <td className="py-3 px-3 text-center">
                      {f.sf ? <Check className="h-5 w-5 text-emerald-500 inline" /> : <X className="h-5 w-5 text-slate-300 inline" />}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {f.comp ? <Check className="h-5 w-5 text-slate-400 inline" /> : <X className="h-5 w-5 text-slate-300 inline" />}
                    </td>
                  </tr>
                ))}
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-3 text-sm font-semibold text-slate-700">Prix d'entrée payant</td>
                  <td className="py-3 px-3 text-center text-emerald-700 font-bold">14€/mois</td>
                  <td className="py-3 px-3 text-center text-slate-500">{c.starting_price > 0 ? `${c.starting_price}€/mois` : 'Gratuit limité'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Long-form pros/cons */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-emerald-800 mb-4">SmartFacture AI</h3>
            <ul className="space-y-2.5">
              {[
                'Format Factur-X natif (conforme 2026)',
                'Copilote IA en français (unique sur le marché)',
                'Scanner IA OCR pour reçus et factures',
                'Plan gratuit 5 factures/mois sans CB',
                'Hébergement France (OVHcloud Roubaix)',
                'Module URSSAF intégré',
                'Simulation PPF/PDP avant la mise en production',
              ].map((p) => (
                <li key={p} className="flex gap-2 text-sm text-slate-700">
                  <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-slate-700 mb-4">{c.name}</h3>
            <p className="text-sm text-slate-600 mb-4">{c.tagline}</p>
            <p className="text-sm text-slate-600 leading-relaxed">{c.weakness}</p>
            <a href={c.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:underline mt-4">
              Site officiel {c.name} ↗
            </a>
          </div>
        </div>
      </section>

      {/* Other comparisons */}
      <section className="bg-white py-12 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Autres comparatifs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/comparaison/smartfacture-vs-${o.slug}`}
                className="bg-emerald-50/40 border border-emerald-100 hover:border-emerald-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:text-emerald-700 transition flex items-center justify-between group"
                data-testid="seo-other-comp-link"
              >
                <span>SmartFacture vs {o.name}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-emerald-500 transition" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            Migrez de {c.name} vers SmartFacture AI
          </h2>
          <p className="text-slate-700 mb-7">
            Importez vos clients et factures en 2 minutes. Essai gratuit sans carte bancaire.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-7 py-3.5 rounded-xl shadow-xl shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
            data-testid="seo-comp-cta-bottom"
          >
            Tester SmartFacture gratuitement <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
